import {
  type ChangeEvent,
  type FormEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { router, useForm } from "@inertiajs/react"
import { ImageIcon, Pencil, Plus, Search, Trash2, UtensilsCrossed } from "lucide-react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { FilterChip } from "@/components/ui/filter-chip"
import { SearchInput } from "@/components/ui/search-input"
import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import { InlineInput } from "@/components/ui/inline-input"
import { InlineSelect } from "@/components/ui/inline-select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"
import { normalizeForSearch } from "@/lib/search"
import { cn } from "@/lib/utils"

import { ExtrasWorkspace, type ExtraGroup } from "@/features/staff/extras-workspace"
import { menuItemMatches } from "@/features/staff/table-detail-workspace"

export type MenuItem = {
  id: number
  name: string
  description: string | null
  price: string
  currency: string
  status: string
  station: string
  parentType: string | null
  parentName: string | null
  categoryId: string | null
  imageUrl: string | null
  emoji: string | null
}

export type MenuCategory = {
  id: number
  name: string
  emoji: string | null
  items: MenuItem[]
}

type Option = { value: string; label: string }

export type MenuWorkspaceProps = {
  categories: MenuCategory[]
  orphanItems: MenuItem[]
  extraGroups: ExtraGroup[]
  statuses: Option[]
  extraStatuses: Option[]
  stations: Option[]
}

const ORPHAN_KEY = "orphans"
const NONE_VALUE = "none"
const ALL_SCOPE = "all"
const EXTRAS_SCOPE = "extras"

// Menu item status carries meaning through a small semantic dot, never through
// large colored text — same rule the table workspace follows.
export const STATUS_DOT: Record<string, string> = {
  active: "bg-status-available",
  sold_out: "bg-status-occupied",
  inactive: "bg-status-disabled",
}

export function statusDotClass(status: string) {
  return STATUS_DOT[status] ?? "bg-status-disabled"
}

export type MenuSectionEntry = {
  key: string
  id: number | null
  name: string
  emoji: string | null
  items: MenuItem[]
}

// Categories in server order, with an "uncategorized" bucket appended only when
// orphan items actually exist — an empty bucket would just be noise.
export function buildMenuSections(
  categories: MenuCategory[],
  orphanItems: MenuItem[],
  orphanLabel: string
): MenuSectionEntry[] {
  const sections: MenuSectionEntry[] = categories.map((category) => ({
    key: `cat-${category.id}`,
    id: category.id,
    name: category.name,
    emoji: category.emoji,
    items: category.items,
  }))

  if (orphanItems.length > 0) {
    sections.push({ key: ORPHAN_KEY, id: null, name: orphanLabel, emoji: null, items: orphanItems })
  }

  return sections
}

/**
 * The three questions a manager asks about a menu that are not "where is this
 * dish": what is off the floor right now, and what did we never finish setting
 * up. Each one is a filter, not a statistic — the count only exists so the chip
 * can say whether pressing it is worth anything.
 */
export type MenuFlag = "sold_out" | "no_photo" | "no_description"

export function menuItemHasFlag(item: MenuItem, flag: MenuFlag): boolean {
  if (flag === "sold_out") return item.status === "sold_out"
  if (flag === "no_photo") return item.imageUrl === null
  // A description of blanks renders as an empty line on the diner menu, so it
  // is missing for every purpose that matters.
  return (item.description ?? "").trim() === ""
}

export function menuHealthCounts(sections: MenuSectionEntry[]) {
  const items = sections.flatMap((section) => section.items)

  return {
    total: items.length,
    soldOut: items.filter((item) => menuItemHasFlag(item, "sold_out")).length,
    withoutPhoto: items.filter((item) => menuItemHasFlag(item, "no_photo")).length,
    withoutDescription: items.filter((item) => menuItemHasFlag(item, "no_description")).length,
  }
}

/**
 * Narrows the whole menu at once: by scope (a tab), by free text, and by one
 * health flag. All three compose.
 *
 * Two rules that are easy to get backwards:
 *  - An empty category stays visible while BROWSING, because it is the only
 *    place to add its first product. It disappears while FILTERING, where an
 *    empty section is just a heading over nothing.
 *  - A section whose own name matches the query keeps all of its items, so
 *    typing "postres" answers with the category rather than with silence.
 */
export function filterMenuSections(
  sections: MenuSectionEntry[],
  { query, scope, flag }: { query: string; scope: string; flag: MenuFlag | null }
): MenuSectionEntry[] {
  // Fold once per keystroke, not once per dish.
  const normalizedQuery = normalizeForSearch(query)
  const narrowing = normalizedQuery !== "" || flag !== null

  return sections
    .filter((section) => scope === ALL_SCOPE || section.key === scope)
    .map((section) => {
      const sectionMatches =
        normalizedQuery === "" || normalizeForSearch(section.name).includes(normalizedQuery)

      return {
        ...section,
        items: section.items.filter(
          (item) =>
            (sectionMatches || menuItemMatches(item, normalizedQuery)) &&
            (flag === null || menuItemHasFlag(item, flag))
        ),
      }
    })
    .filter((section) => !narrowing || section.items.length > 0)
}

type DrawerState =
  | { mode: "create"; categoryId: string | null }
  | { mode: "edit"; item: MenuItem }
  | null

// Inertia's FormData builder appends no key at all for an empty array, so
// posting the item form with `forceFormData` (required for the image field)
// would silently drop `extra_group_ids` the moment staff unchecks every
// group — the backend's `sync_extra_groups` no-ops when the key is absent,
// leaving stale attachments in place despite a success toast. A single
// blank-string sentinel keeps the key alive in the multipart body;
// `sync_extra_groups` already discards blanks via `.map(&:presence).compact`,
// so `[""]` resolves to "detach all" exactly like `[]` would if FormData
// could carry it. Also spoofs `_method: patch` on edit (Rack reads a PATCH
// back out of a POST body that way).
export function itemFormTransform<T extends { extra_group_ids: string[] }>(
  data: T,
  editing: boolean
): T & { _method?: string } {
  return {
    ...data,
    extra_group_ids: data.extra_group_ids.length === 0 ? [""] : data.extra_group_ids,
    ...(editing ? { _method: "patch" } : {}),
  }
}

export function MenuWorkspace({
  categories,
  orphanItems,
  extraGroups,
  statuses,
  extraStatuses,
  stations,
}: MenuWorkspaceProps) {
  const sections = useMemo(
    () =>
      buildMenuSections(
        categories,
        orphanItems,
        t("ui.react.pages.staff.location.manage.menu.index.no_category")
      ),
    [categories, orphanItems]
  )
  const health = useMemo(() => menuHealthCounts(sections), [sections])

  const [query, setQuery] = useState("")
  const [scope, setScope] = useState<string>(ALL_SCOPE)
  const [flag, setFlag] = useState<MenuFlag | null>(null)
  const [drawer, setDrawer] = useState<DrawerState>(null)

  const newCategoryAnchor = useRef<HTMLFormElement>(null)
  const newCategoryInput = useRef<HTMLInputElement>(null)

  // Keep the scope valid as categories are added and removed across reloads.
  useEffect(() => {
    const known =
      scope === ALL_SCOPE ||
      scope === EXTRAS_SCOPE ||
      sections.some((section) => section.key === scope)

    if (!known) setScope(ALL_SCOPE)
  }, [sections, scope])

  const visibleSections = useMemo(
    () =>
      scope === EXTRAS_SCOPE ? [] : filterMenuSections(sections, { query, scope, flag }),
    [sections, query, scope, flag]
  )

  // Extras are not searched — they carry no description and no photo, so no
  // health flag has anything to say about them either. Rather than let a query
  // silently hide them, they render only when nothing is narrowing the products
  // or when their own tab is the scope.
  const showExtras =
    scope === EXTRAS_SCOPE || (scope === ALL_SCOPE && query === "" && flag === null)
  const noResults = visibleSections.length === 0

  // The button beside the tabs does not create anything: it takes you to the
  // form that does. With a handful of categories that form is already on
  // screen; with twenty it is a scroll away and nobody finds it.
  const jumpToNewCategory = () => {
    newCategoryAnchor.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "end",
    })
    // `preventScroll` leaves the smooth scroll above in charge; without it the
    // browser jumps to the field first and the animation never reads.
    newCategoryInput.current?.focus({ preventScroll: true })
  }

  return (
    <MainContainer size="md">
      <div data-testid="menu-management-view" className="space-y-4">
        <PageHeader
          title={t("ui.react.pages.staff.location.manage.menu.index.management_menu")}
          description={t(
            "ui.react.pages.staff.location.manage.menu.index.categories_items_organized_section"
          )}
        />

        <Card density="compact" className="gap-0 overflow-hidden">
          <MenuBrowserHeader
            sections={sections}
            health={health}
            query={query}
            onQueryChange={setQuery}
            scope={scope}
            onScopeChange={setScope}
            flag={flag}
            onFlagChange={setFlag}
            extraGroupCount={extraGroups.length}
            onJumpToNewCategory={jumpToNewCategory}
          />

          {scope !== EXTRAS_SCOPE && (
          <CardContent className="space-y-7 pt-4">
            {noResults ? (
              <EmptyResults hasMenu={health.total > 0} />
            ) : (
              visibleSections.map((section) => (
                <MenuSectionBlock
                  key={section.key}
                  section={section}
                  statuses={statuses}
                  stations={stations}
                  onAddItem={() =>
                    setDrawer({
                      mode: "create",
                      categoryId: section.id === null ? null : String(section.id),
                    })
                  }
                  onEditItem={(item) => setDrawer({ mode: "edit", item })}
                />
              ))
            )}

            <AddCategoryForm anchorRef={newCategoryAnchor} nameRef={newCategoryInput} />
          </CardContent>
          )}
        </Card>

        {showExtras && (
          <ExtrasWorkspace groups={extraGroups} extraStatuses={extraStatuses} />
        )}
      </div>

      {drawer && (
        <ItemFormDrawer
          key={drawer.mode === "edit" ? `edit-${drawer.item.id}` : "create"}
          state={drawer}
          categories={categories}
          extraGroups={extraGroups}
          statuses={statuses}
          stations={stations}
          onClose={() => setDrawer(null)}
        />
      )}
    </MainContainer>
  )
}

/**
 * Search, health filters and the category tabs — the whole browsing surface of
 * the menu, mirroring `TableMenuBrowser` so the screen a manager configures and
 * the screen a waiter orders from read the same way.
 *
 * The tab strip scrolls; the "new category" button does not. It sits outside the
 * scrolling container precisely so it stays reachable once the strip overflows,
 * which is the moment the inline form at the bottom stops being findable.
 */
function MenuBrowserHeader({
  sections,
  health,
  query,
  onQueryChange,
  scope,
  onScopeChange,
  flag,
  onFlagChange,
  extraGroupCount,
  onJumpToNewCategory,
}: {
  sections: MenuSectionEntry[]
  health: ReturnType<typeof menuHealthCounts>
  query: string
  onQueryChange: (value: string) => void
  scope: string
  onScopeChange: (value: string) => void
  flag: MenuFlag | null
  onFlagChange: (value: MenuFlag | null) => void
  extraGroupCount: number
  onJumpToNewCategory: () => void
}) {
  const toggleFlag = (next: MenuFlag) => onFlagChange(flag === next ? null : next)

  return (
    <CardHeader className="flex flex-col items-stretch gap-3 border-b pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onClear={() => onQueryChange("")}
          placeholder={t("ui.react.pages.staff.location.manage.menu.index.search_placeholder")}
          containerClassName="w-full sm:w-80"
          data-testid="menu-search-input"
        />

        <FilterChip
          active={flag === "sold_out"}
          tone="occupied"
          count={health.soldOut}
          onClick={() => toggleFlag("sold_out")}
          data-testid="menu-filter-sold-out"
        >
          {t("ui.react.pages.staff.location.manage.menu.index.filter_sold_out")}
        </FilterChip>
        <FilterChip
          active={flag === "no_photo"}
          count={health.withoutPhoto}
          onClick={() => toggleFlag("no_photo")}
          data-testid="menu-filter-no-photo"
        >
          {t("ui.react.pages.staff.location.manage.menu.index.filter_without_photo")}
        </FilterChip>
        <FilterChip
          active={flag === "no_description"}
          count={health.withoutDescription}
          onClick={() => toggleFlag("no_description")}
          data-testid="menu-filter-no-description"
        >
          {t("ui.react.pages.staff.location.manage.menu.index.filter_without_description")}
        </FilterChip>
      </div>

      <div className="flex items-stretch">
        <div
          className="-mb-px flex min-w-0 flex-1 gap-0.5 overflow-x-auto border-b"
          role="group"
          aria-label={t("ui.react.pages.staff.location.manage.menu.index.categories_title")}
        >
          <MenuTab
            scopeKey={ALL_SCOPE}
            label={t("ui.react.pages.staff.location.manage.menu.index.all_products")}
            count={health.total}
            active={scope === ALL_SCOPE}
            onSelect={onScopeChange}
          />
          {sections.map((section) => (
            <MenuTab
              key={section.key}
              scopeKey={section.key}
              label={section.name}
              emoji={section.emoji}
              count={section.items.length}
              active={scope === section.key}
              onSelect={onScopeChange}
            />
          ))}
          {extraGroupCount > 0 && (
            <>
              <span aria-hidden="true" className="my-2 w-px shrink-0 bg-border" />
              <MenuTab
                scopeKey={EXTRAS_SCOPE}
                label={t("ui.react.pages.staff.location.manage.menu.index.extras_title")}
                count={extraGroupCount}
                active={scope === EXTRAS_SCOPE}
                onSelect={onScopeChange}
              />
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 border-b pl-2">
          <span aria-hidden="true" className="h-5 w-px bg-border" />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onJumpToNewCategory}
            data-testid="jump-new-category-button"
          >
            <Plus />
            {t("ui.react.pages.staff.location.manage.menu.index.jump_to_new_category")}
          </Button>
        </div>
      </div>
    </CardHeader>
  )
}

function MenuTab({
  scopeKey,
  label,
  emoji,
  count,
  active,
  onSelect,
}: {
  scopeKey: string
  label: string
  emoji?: string | null
  count: number
  active: boolean
  onSelect: (key: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(scopeKey)}
      aria-pressed={active}
      data-testid={`menu-category-tab-${scopeKey}`}
      className={cn(
        "flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {emoji && <span aria-hidden="true">{emoji}</span>}
      {label}
      <span
        className={cn(
          "text-xs tabular-nums text-muted-foreground",
          count === 0 && "text-muted-foreground/40"
        )}
      >
        {count}
      </span>
    </button>
  )
}

function EmptyResults({ hasMenu }: { hasMenu: boolean }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {hasMenu ? <Search className="size-5" /> : <UtensilsCrossed className="size-5" />}
      </div>
      <p className="text-sm font-semibold">
        {hasMenu
          ? t("ui.react.pages.staff.location.manage.menu.index.no_results_title")
          : t("ui.react.pages.staff.location.manage.menu.index.no_menu_items")}
      </p>
      {hasMenu && (
        <p className="text-xs text-muted-foreground">
          {t("ui.react.pages.staff.location.manage.menu.index.no_results_hint")}
        </p>
      )}
    </div>
  )
}

/**
 * The one place a category is born. `anchorRef` and `nameRef` are what the
 * header button scrolls to and focuses — the button is a shortcut to this form,
 * never a second way to create.
 */
function AddCategoryForm({
  anchorRef,
  nameRef,
}: {
  anchorRef: RefObject<HTMLFormElement | null>
  nameRef: RefObject<HTMLInputElement | null>
}) {
  const { data, setData, post, processing, errors } = useForm({ name: "", emoji: "" })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    post("/manage/categories", {
      preserveScroll: true,
      // The page keeps scope, query and filter in local state; without this
      // the component remounts and a manager loses the search they were in the
      // middle of just because they added a category.
      preserveState: true,
      // Clear the fields explicitly rather than with `reset()`. Inertia rebases
      // a form's defaults onto the data it just submitted, so `reset()` here
      // restored the PREVIOUS category's name: create "Entradas" then "Carnes"
      // and the field came back holding "Entradas". Measured, one create behind,
      // every time. This predates the redesign.
      onSuccess: () => setData({ name: "", emoji: "" }),
    })
  }

  return (
    <form onSubmit={submit} ref={anchorRef} className="space-y-1 border-t pt-4">
      <div className="flex items-center gap-1">
        <Input
          value={data.emoji}
          onChange={(event) => setData("emoji", event.target.value)}
          placeholder="🏷️"
          maxLength={16}
          className="w-12 shrink-0 px-0 text-center text-lg"
          data-testid="add-category-emoji-input"
          aria-label={t("ui.react.pages.staff.location.manage.menu.index.category_emoji")}
        />
        <Input
          ref={nameRef}
          value={data.name}
          onChange={(event) => setData("name", event.target.value)}
          placeholder={t("ui.react.pages.staff.location.manage.menu.index.add_category_placeholder")}
          className="sm:max-w-xs"
          data-testid="add-category-input"
          aria-label={t("ui.react.pages.staff.location.manage.menu.index.add_category")}
        />
        <Button
          type="submit"
          size="icon"
          variant="outline"
          disabled={processing || data.name.trim() === ""}
          data-testid="add-category-button"
          aria-label={t("ui.react.pages.staff.location.manage.menu.index.add_category")}
        >
          <Plus />
        </Button>
      </div>
      {errors.name && (
        <p className="px-1 text-xs font-medium text-destructive">{errors.name}</p>
      )}
    </form>
  )
}

/**
 * One category, heading plus its products — the `MenuSection` shape from the
 * table workspace, with the controls that used to live in the panel header
 * folded into the heading itself. That fold is the whole point of the redesign:
 * the category and its dishes are one object on screen, not two cards.
 *
 * `panel-add-item-button` is deliberately not unique across the page: every
 * section owns one. Selecting a tab renders exactly one section, which is the
 * state every Playwright helper reaches it from.
 */
function MenuSectionBlock({
  section,
  statuses,
  stations,
  onAddItem,
  onEditItem,
}: {
  section: MenuSectionEntry
  statuses: Option[]
  stations: Option[]
  onAddItem: () => void
  onEditItem: (item: MenuItem) => void
}) {
  const isOrphan = section.key === ORPHAN_KEY
  const soldOut = section.items.filter((item) => item.status === "sold_out").length

  return (
    <section data-testid={`menu-section-${section.key}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {isOrphan ? (
          <h3 className="text-base font-semibold tracking-tight">{section.name}</h3>
        ) : (
          <>
            <InlineInput
              value={section.emoji ?? ""}
              onSubmit={(emoji) =>
                patchInline(`/manage/categories/${section.id}`, { emoji }, { preserveState: true })
              }
              placeholder="🏷️"
              className="w-12 border-transparent bg-transparent px-0 text-center text-lg shadow-none hover:border-input focus-visible:border-ring"
              data-testid={`category-emoji-${section.id}`}
              aria-label={t("ui.react.pages.staff.location.manage.menu.index.category_emoji")}
            />
            {/* Borderless until you reach for it: the name has to read as the
                section heading it is, while staying the same inline field it
                has always been. */}
            <InlineInput
              value={section.name}
              onSubmit={(name) =>
                patchInline(`/manage/categories/${section.id}`, { name }, { preserveState: true })
              }
              className="w-full border-transparent bg-transparent px-2 text-base font-semibold tracking-tight shadow-none hover:border-input focus-visible:border-ring sm:w-56"
              data-testid={`rename-category-${section.id}`}
              aria-label={t("ui.react.pages.staff.location.manage.menu.index.rename_category")}
            />
          </>
        )}

        <span className="text-xs text-muted-foreground">
          {t(
            section.items.length === 1
              ? "ui.react.pages.staff.location.manage.menu.index.dish_count_one"
              : "ui.react.pages.staff.location.manage.menu.index.dish_count_other",
            { count: section.items.length }
          )}
          {soldOut > 0 &&
            ` · ${t(
              soldOut === 1
                ? "ui.react.pages.staff.location.manage.menu.index.sold_out_count_one"
                : "ui.react.pages.staff.location.manage.menu.index.sold_out_count_other",
              { count: soldOut }
            )}`}
        </span>

        <div className="ml-auto flex items-center gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={onAddItem} data-testid="panel-add-item-button">
            <Plus />
            {t("ui.react.pages.staff.location.manage.menu.index.add_item")}
          </Button>
          {!isOrphan && section.id != null && <DeleteCategoryButton categoryId={section.id} />}
        </div>
      </div>

      {section.items.length === 0 ? (
        <p className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground">
          {t("ui.react.pages.staff.location.manage.menu.index.empty_category")}
        </p>
      ) : (
        <ul className="divide-y overflow-hidden rounded-lg border">
          {section.items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              statuses={statuses}
              stations={stations}
              onEdit={() => onEditItem(item)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function ItemRow({
  item,
  statuses,
  stations,
  onEdit,
}: {
  item: MenuItem
  statuses: Option[]
  stations: Option[]
  onEdit: () => void
}) {
  const isSoldOut = item.status === "sold_out"
  // The station enum used to be printed raw ("kitchen") even though the server
  // already ships the localized label beside it. Fall back to the enum only if
  // the catalog ever stops carrying the value.
  const stationLabel = stations.find((station) => station.value === item.station)?.label

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-3 bg-card px-3 py-2.5",
        isSoldOut && "opacity-80"
      )}
      data-testid={`menu-item-row-${item.id}`}
    >
      <span
        className={cn("size-2 shrink-0 rounded-full", statusDotClass(item.status))}
        aria-hidden
      />

      <button
        type="button"
        onClick={onEdit}
        className="min-w-0 flex-1 text-left"
        data-testid={`menu-item-name-${item.id}`}
      >
        <span className="block truncate text-sm font-semibold tracking-tight hover:underline">
          {item.name}
        </span>
        {item.description && (
          <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
        )}
      </button>

      {/* The diner menu leads with the photo, so its absence is a fact about
          the product worth seeing from the list. Same small uppercase mark the
          table workspace uses to say a dish is blocked. */}
      {!item.imageUrl && (
        <span className="hidden shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:inline-flex">
          <ImageIcon className="size-3" />
          {t("ui.react.pages.staff.location.manage.menu.index.filter_without_photo")}
        </span>
      )}

      {stationLabel && (
        <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">
          {stationLabel}
        </Badge>
      )}

      <div className="flex shrink-0 items-center gap-1">
        <span className="text-xs text-muted-foreground">{item.currency.toUpperCase()}</span>
        <InlineInput
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={item.price}
          onSubmit={(price) =>
            patchInline(`/manage/items/${item.id}`, { price }, { preserveState: true })
          }
          className="h-10 w-24 tabular-nums md:h-8"
          data-testid={`menu-item-price-${item.id}`}
          aria-label={t("ui.react.pages.staff.location.manage.items.new.price")}
        />
      </div>

      <InlineSelect
        value={item.status}
        options={statuses.map((status) => ({
          value: status.value,
          label: status.label,
          testId: `menu-item-status-${item.id}-option-${status.value}`,
        }))}
        onSubmit={(status) =>
          patchInline(`/manage/items/${item.id}`, { status }, { preserveState: true })
        }
        size="sm"
        className="w-full shrink-0 sm:w-32"
        data-testid={`menu-item-status-${item.id}`}
      />

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onEdit}
          data-testid={`edit-item-${item.id}`}
          aria-label={t("ui.react.pages.staff.location.manage.menu.index.edit_item")}
        >
          <Pencil />
        </Button>
        <DeleteItemButton itemId={item.id} />
      </div>
    </li>
  )
}

function DeleteCategoryButton({ categoryId }: { categoryId: number }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen(true)}
        data-testid={`delete-category-${categoryId}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.delete_category")}
      >
        <Trash2 className="text-destructive" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`delete-category-dialog-${categoryId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_category")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.staff.tables.delete_category_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-category-dialog-cancel-${categoryId}`}>
              {t("ui.common.actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                router.delete(`/manage/categories/${categoryId}`, {
                  preserveScroll: true,
                  preserveState: true,
                })
              }
              data-testid={`delete-category-dialog-confirm-${categoryId}`}
            >
              {t("ui.react.pages.staff.location.manage.menu.index.delete_category")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function DeleteItemButton({ itemId }: { itemId: number }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen(true)}
        data-testid={`delete-item-${itemId}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.delete_item")}
      >
        <Trash2 className="text-destructive" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`delete-item-dialog-${itemId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_item")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_item_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-item-dialog-cancel-${itemId}`}>
              {t("ui.common.actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                router.delete(`/manage/items/${itemId}`, {
                  preserveScroll: true,
                  preserveState: true,
                })
              }
              data-testid={`delete-item-dialog-confirm-${itemId}`}
            >
              {t("ui.react.pages.staff.location.manage.menu.index.delete_item")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function ItemFormDrawer({
  state,
  categories,
  extraGroups,
  statuses,
  stations,
  onClose,
}: {
  state: NonNullable<DrawerState>
  categories: MenuCategory[]
  extraGroups: ExtraGroup[]
  statuses: Option[]
  stations: Option[]
  onClose: () => void
}) {
  const editing = state.mode === "edit"
  const item = editing ? state.item : null

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const { data, setData, post, transform, processing, errors } = useForm<{
    name: string
    description: string
    price: string
    currency: string
    status: string
    station: string
    category_id: string
    extra_group_ids: string[]
    image: File | null
    emoji: string
  }>({
    name: item?.name ?? "",
    description: item?.description ?? "",
    price: item?.price ?? "",
    currency: item?.currency ?? "mxn",
    status: item?.status ?? statuses[0]?.value ?? "active",
    station: item?.station ?? stations[0]?.value ?? "kitchen",
    category_id: item?.categoryId ?? (state.mode === "create" ? state.categoryId ?? "" : ""),
    extra_group_ids: item
      ? extraGroups.filter((group) => group.itemIds.includes(String(item.id))).map((group) => String(group.id))
      : [],
    image: null,
    emoji: item?.emoji ?? "",
  })

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const imageSrc = previewUrl ?? item?.imageUrl ?? null
  const errorBag = errors as Record<string, string | undefined>
  const categoryError = errorBag.parent ?? errorBag.category_id

  const toggleExtraGroup = (groupId: string, checked: boolean) => {
    setData(
      "extra_group_ids",
      checked ? [...data.extra_group_ids, groupId] : data.extra_group_ids.filter((id) => id !== groupId)
    )
  }

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    setData("image", file)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(file ? URL.createObjectURL(file) : null)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    const url = editing ? `/manage/items/${item!.id}` : "/manage/items"
    transform((current) => itemFormTransform(current, editing))

    post(url, {
      forceFormData: true,
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        toast.success(
          t(
            editing
              ? "ui.react.pages.staff.location.manage.menu.index.item_updated"
              : "ui.react.pages.staff.location.manage.menu.index.item_created"
          )
        )
        onClose()
      },
    })
  }

  return (
    <ResponsiveDrawer open onOpenChange={(next) => !next && onClose()}>
      <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>
            {editing
              ? t("ui.react.pages.staff.location.manage.menu.index.edit_item_title")
              : t("ui.react.pages.staff.location.manage.items.new.new_item")}
          </DrawerTitle>
          <DrawerDescription>
            {t("ui.react.pages.staff.location.manage.items.new.create_item_menu")}
          </DrawerDescription>
        </DrawerHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-testid="item-form"
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4">
            <div className="space-y-2">
              <Label htmlFor="item-name">
                {t("ui.react.pages.staff.location.manage.items.new.name")}
              </Label>
              <Input
                id="item-name"
                value={data.name}
                onChange={(event) => setData("name", event.target.value)}
                placeholder={t("ui.react.pages.staff.location.manage.items.new.taco_pastor")}
                data-testid="item-name-input"
                required
              />
              {errors.name && (
                <p className="text-sm font-medium text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="item-emoji">
                {t("ui.react.pages.staff.location.manage.items.new.emoji")}
              </Label>
              <Input
                id="item-emoji"
                value={data.emoji}
                onChange={(event) => setData("emoji", event.target.value)}
                placeholder="🌮"
                maxLength={16}
                className="w-16 text-center text-lg"
                data-testid="item-emoji-input"
              />
              <p className="text-xs text-muted-foreground">
                {t("ui.react.pages.staff.location.manage.items.new.emoji_hint")}
              </p>
              {errors.emoji && (
                <p className="text-sm font-medium text-destructive">{errors.emoji}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="item-price">
                  {t("ui.react.pages.staff.location.manage.items.new.price")}
                </Label>
                <Input
                  id="item-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={data.price}
                  onChange={(event) => setData("price", event.target.value)}
                  placeholder="0.00"
                  data-testid="item-price-input"
                  required
                />
                {errors.price && (
                  <p className="text-sm font-medium text-destructive">{errors.price}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="item-currency">
                  {t("ui.react.pages.staff.location.manage.items.new.currency")}
                </Label>
                <Select value={data.currency} onValueChange={(value) => setData("currency", value)}>
                  <SelectTrigger id="item-currency" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mxn">MXN</SelectItem>
                    <SelectItem value="usd">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="item-station">
                  {t("ui.react.pages.staff.location.manage.items.new.station_primary")}
                </Label>
                <Select value={data.station} onValueChange={(value) => setData("station", value)}>
                  <SelectTrigger id="item-station" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map((station) => (
                      <SelectItem key={station.value} value={station.value}>
                        {station.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="item-status">
                  {t("ui.react.pages.staff.location.manage.items.new.status")}
                </Label>
                <Select value={data.status} onValueChange={(value) => setData("status", value)}>
                  <SelectTrigger id="item-status" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="item-category">
                {t("ui.react.pages.staff.location.manage.items.new.category")}
              </Label>
              <Select
                value={data.category_id || NONE_VALUE}
                onValueChange={(value) => setData("category_id", value === NONE_VALUE ? "" : value)}
              >
                <SelectTrigger id="item-category" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>
                    {t("ui.react.pages.staff.location.manage.items.new.category_2")}
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categoryError && (
                <p className="text-sm font-medium text-destructive">{categoryError}</p>
              )}
            </div>

            {extraGroups.length > 0 && (
              <div className="space-y-2">
                <Label>{t("ui.react.pages.staff.location.manage.items.new.extra_groups")}</Label>
                <div className="space-y-1.5 rounded-md border p-2">
                  {extraGroups.map((group) => {
                    const checked = data.extra_group_ids.includes(String(group.id))
                    return (
                      <label
                        key={group.id}
                        className="flex cursor-pointer items-center justify-between gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50"
                      >
                        <span>{group.name}</span>
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          checked={checked}
                          onChange={(event) => toggleExtraGroup(String(group.id), event.target.checked)}
                          data-testid={`item-extra-group-${group.id}`}
                        />
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="item-description">
                {t("ui.react.pages.staff.location.manage.items.new.description")}
              </Label>
              <Input
                id="item-description"
                value={data.description}
                onChange={(event) => setData("description", event.target.value)}
                placeholder={t(
                  "ui.react.pages.staff.location.manage.items.new.tortilla_corn_meat_marinada_pineapple"
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="item-image">
                {t("ui.react.pages.staff.location.manage.items.new.photo_item")}
              </Label>
              <Input
                id="item-image"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={handleImageChange}
                data-testid="item-image-input"
              />
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={data.name}
                  className="size-24 rounded-lg border object-cover"
                  data-testid="item-image-preview"
                />
              ) : (
                <div className="flex size-24 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
                  <ImageIcon className="size-5" />
                </div>
              )}
              {errors.image && (
                <p className="text-sm font-medium text-destructive">{errors.image}</p>
              )}
            </div>
          </div>

          <DrawerFooter className="flex-row justify-end gap-2 border-t">
            <DrawerClose asChild>
              <Button type="button" variant="outline">
                {t("ui.react.pages.staff.location.manage.items.new.cancel")}
              </Button>
            </DrawerClose>
            <Button type="submit" disabled={processing} data-testid="save-item-button">
              {editing
                ? t("ui.react.components.ui.inline_update.save")
                : t("ui.react.pages.staff.location.manage.items.new.create_item")}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}
